import {
  syncArea,
  assignSupervisor,
  assignAreaController,
  syncProvinces,
  createArea,
} from '@/api/area-management-api'
import {
  API_SUCCESS,
  API_PENDING,
  API_FAILURE,
  RESET_INITIAL_STATE,
  INTERNAL_SERVER_ERROR,
  UNPROCESSABLE_ENTITY,
} from '@/constants/Actions'

export type AreaManagementState = {
  loading: boolean
  error: boolean
  errorType: string
  errorMessage: string
  success: boolean
  successMessage: string
}

export const areaManagementInitialState: AreaManagementState = {
  loading: true,
  error: false,
  errorType: '',
  errorMessage: '',
  success: false,
  successMessage: '',
}

export type AreaManagementAction =
  | { type: typeof API_PENDING }
  | { type: typeof API_FAILURE; payload: { type: string; message: string } }
  | { type: typeof API_SUCCESS; payload: { message: string } }
  | { type: typeof RESET_INITIAL_STATE }

export const areaManagementReducer = (
  state: AreaManagementState,
  action: AreaManagementAction,
): AreaManagementState => {
  const { type } = action

  switch (type) {
    case API_PENDING:
      return {
        ...state,
        loading: true,
        error: false,
        errorType: '',
        errorMessage: '',
        success: false,
        successMessage: '',
      }
    case API_FAILURE:
      if ('payload' in action) {
        return {
          ...state,
          loading: false,
          error: true,
          errorType: action.payload.type,
          errorMessage: action.payload.message,
        }
      }
      return state
    case API_SUCCESS:
      if ('payload' in action) {
        return {
          ...state,
          loading: false,
          success: true,
          successMessage: action.payload.message,
        }
      }
      return state
    case RESET_INITIAL_STATE:
      return {
        ...state,
        loading: false,
        error: false,
        success: false,
        successMessage: '',
        errorMessage: '',
      }
    default:
      return state
  }
}

export const areaManagementActions = {
  syncProvinces:
    (data: any) => async (dispatch: (action: AreaManagementAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await syncProvinces(data)
        if (!response) {
          throw {
            message: 'Failed to sync provinces. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to sync provinces.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Provinces synced successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: { type: 'API_ERROR', message: 'Failed to sync area' },
        })
      }
    },
  syncArea:
    (data: any) => async (dispatch: (action: AreaManagementAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await syncArea(data)
        if (!response) {
          throw {
            message: 'Failed to sync area. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to sync area.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Area synced successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: { type: 'API_ERROR', message: 'Failed to sync area' },
        })
      }
    },

  assignSupervisor:
    (data: any) => async (dispatch: (action: AreaManagementAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await assignSupervisor(data)
        if (!response) {
          throw {
            message: 'Failed to assign supervisor. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to assign supervisor.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Supervisor assigned successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to assign supervisor',
          },
        })
      }
    },

  assignAreaController:
    (data: any) => async (dispatch: (action: AreaManagementAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await assignAreaController(data)
        if (!response) {
          throw {
            message:
              'Failed to assign area controller. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to assign area controller.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Area controller assigned successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to assign area controller',
          },
        })
      }
    },
  addArea:
    (data: any, setError: React.Dispatch<React.SetStateAction<string>>) => async (dispatch: (action: AreaManagementAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        await createArea(data).then((response) => {
          if (!response) {
            throw {
              message: 'Failed to send data. Please check your network.',
              type: 'API/Network failure',
            }
          } else if (response.data.status === INTERNAL_SERVER_ERROR) {
            throw {
              message: 'Failed to create area.',
              type: response.data.message,
            }
          } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
            setError(response.data.httpStatus)
            throw {
              message: response.data.message,
              type: response.data.message,
            }
          }
        })
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Created area successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: { type: 'API_ERROR', message: 'Failed to submit' },
        })
      }
    },
}
